import React, { ReactNode } from 'react';

import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import BackButton from 'src/components/back-button/BackButton';
import GlobalFooter from 'src/components/navigation/GlobalFooter';
import { DLSSize } from 'src/types/dls-size.enum';
import colors from 'src/styles/colors.module.scss';

import styles from './wideView.module.scss';
import dlsContentStyles from 'src/styles/dlsContentStyles.module.scss';
import useDocumentTitle from 'src/hooks/useDocumentTitle';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import ReviewTransactionsBanner from 'src/components/review-transactions-banner/ReviewTransactionsBanner';
import { SyncBanner } from 'src/components/sync-banner/SyncBanner';
import Selectors from 'src/utils/selectors';
import H from 'history';
import ScrollRestoration from 'src/components/atomic/atoms/scrollRestoration/ScrollRestoration';

type Props = {
    hasBackgroundBanner?: boolean;
    disableNotificationBanners?: boolean;
    showFooter?: boolean;
    hideBackButton?: boolean;
    history?: H.History;
    handleBack?: (...args: any[]) => any;
    classNameContainer?: string;
    classNameView?: string;
    headerChildren?: ReactNode;
    additionalHeaderRows?: ReactNode;
    children: ReactNode;
    preTitle?: ReactNode;
    withDocTitle?: boolean;
    showTermsAndConditions?: boolean;
    scrollRestoration?: boolean;
} & (
    | {
          title?: string;
          titleElement?: never;
      }
    | {
          title?: never;
          titleElement?: JSX.Element;
      }
) &
    (
        | {
              subtitle?: string;
              subtitleElement?: never;
          }
        | {
              subtitle?: never;
              subtitleElement?: JSX.Element;
          }
    );

/**
 * Assuming the provided [titleElement] is a <Typography> element,
 * extract the string content to use as the page title.
 *
 * @param titleElement The JSX element to obtain the text from
 */
function getTypographyTitle(titleElement: JSX.Element): string {
    if (!titleElement) return '';
    if (typeof titleElement === 'string') return titleElement;

    const children = titleElement?.props?.children;

    if (children instanceof Array) {
        return children.map(getTypographyTitle).join('');
    } else return getTypographyTitle(children);
}

export default function WideView({
    preTitle,
    title,
    titleElement,
    subtitle,
    subtitleElement,
    hasBackgroundBanner,
    disableNotificationBanners,
    showFooter,
    hideBackButton,
    history,
    handleBack,
    classNameContainer,
    classNameView,
    headerChildren,
    additionalHeaderRows,
    children,
    withDocTitle = true,
    showTermsAndConditions,
    ...props
}: Props): JSX.Element {
    const { isMinWidthMedium } = useBreakpoints();
    withDocTitle && useDocumentTitle(title ?? getTypographyTitle(titleElement));

    let allClassNamesContainer: string = styles.scrollContainer;
    if (classNameContainer) allClassNamesContainer += ` ${classNameContainer}`;

    let allClassNamesView: string = `${styles.view} ${dlsContentStyles.dlsView}`;
    if (classNameView) allClassNamesView += ` ${classNameView}`;

    const renderTitle: JSX.Element = title ? (
        <Typography
            component="h1"
            variant={isMinWidthMedium ? TypographyVariants.HEADING_XL : TypographyVariants.HEADING_M}
            className={colors.textLight}
            data-cy={Selectors.General.WIDE_VIEW_TITLE}
        >
            {title}
        </Typography>
    ) : (
        titleElement
    );

    const renderSubtitle: JSX.Element = subtitle ? (
        <Typography
            component="p"
            variant={isMinWidthMedium ? TypographyVariants.HEADING_S : TypographyVariants.SUBTEXT}
            className={styles.subtitle}
        >
            {subtitle}
        </Typography>
    ) : (
        subtitleElement
    );

    return (
        <div className={allClassNamesContainer} id="scrollContainer" {...props}>
            <ScrollRestoration scrollElementId="scrollContainer" />
            <main className={allClassNamesView} id="mainContent">
                <div className={styles.headerSection}>
                    {!disableNotificationBanners && (
                        <div className={styles.notificationBanners}>
                            <SyncBanner />
                            <ReviewTransactionsBanner />
                        </div>
                    )}
                    <div className={styles.header}>
                        {preTitle}
                        <div className={styles.left}>
                            <div className={styles.titleContainer}>
                                <div className={styles.titleWrapper}>
                                    {!hideBackButton ? (
                                        <BackButton
                                            history={history}
                                            handleBack={handleBack}
                                            onDarkBackground={hasBackgroundBanner}
                                            size={isMinWidthMedium ? DLSSize.MEDIUM : DLSSize.SMALL}
                                        />
                                    ) : null}
                                    {renderTitle}
                                </div>
                                {renderSubtitle}
                            </div>
                        </div>
                        {headerChildren ? <div className={styles.right}>{headerChildren}</div> : null}
                    </div>
                    {additionalHeaderRows}
                </div>
                {children}
            </main>
            {showFooter && <GlobalFooter showTermsAndConditions={showTermsAndConditions} />}
        </div>
    );
}
